import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IPlatformUser } from '@platform/dao';
import { useDeactivateUserMutation, useReactivateUserMutation } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { toastShown } from '@platform/state/slices/uiSlice';
import { accountErrorKey } from './accountErrorKey';
import type { IAccountActions } from './IAccountActions';

/**
 * Деактивація й відновлення облікового запису (рішення 5 етапу 7): підтвердження з ім'ям особи (UIUX confirmations), запит, повідомлення про
 * результат; помилка сервера (власний запис, останній адміністратор) — зрозумілим текстом.
 */
export function useAccountActions(): IAccountActions {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [deactivate] = useDeactivateUserMutation();
  const [reactivate] = useReactivateUserMutation();
  const [busy, setBusy] = useState<string | null>(null);
  const toggle = useCallback((user: IPlatformUser) => {
    const off = user.active !== false;
    if (!window.confirm(t(off ? 'sysAccounts.confirmDeactivate' : 'sysAccounts.confirmReactivate', { name: user.name }))) return;
    setBusy(user.id);
    (off ? deactivate(user.id) : reactivate(user.id)).unwrap()
      .then(() => dispatch(toastShown({ kind: 'success', text: t(off ? 'sysAccounts.deactivatedToast' : 'sysAccounts.reactivatedToast', { name: user.name }) })))
      .catch((e: unknown) => dispatch(toastShown({ kind: 'error', text: t(accountErrorKey(e)) })))
      .finally(() => setBusy(null));
  }, [deactivate, dispatch, reactivate, t]);
  return { busy, toggle };
}
